/**
 * Routes.
 *
 * The shell owns `/`, the directory and administration. Each application owns its own subtree and
 * is mounted at the path its registry entry names. Route guards decide what renders; the backend
 * decides what the user can actually read.
 */
import { lazy, Suspense } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';

import { AppShell } from '@/platform/shell/AppShell';
import { ProtectedRoute } from '@/auth/ProtectedRoute';
import { censusRoutes } from '@/apps/census/routes';
import { proposalsRoutes } from '@/apps/proposals/routes';
import { trenchingRoutes } from '@/apps/trenching/routes';
import { packetsRoutes } from '@/apps/packets/routes';
import { presentationsRoutes } from '@/apps/presentations/routes';
import { rfpRoutes } from '@/apps/rfp/routes';

const SignInPage = lazy(() => import('@/platform/SignInPage'));
const HomePage = lazy(() => import('@/platform/home/HomePage'));
const EmployersPage = lazy(() => import('@/platform/directory/EmployersPage'));
const EmployerDetailPage = lazy(() => import('@/platform/directory/EmployerDetailPage'));
const CarriersPage = lazy(() => import('@/platform/directory/CarriersPage'));
const CarrierDetailPage = lazy(() => import('@/platform/directory/CarrierDetailPage'));
const CarrierFinderPage = lazy(() => import('@/platform/directory/CarrierFinderPage'));
const EmployerFormPage = lazy(() => import('@/platform/directory/EmployerFormPage'));
const CarrierFormPage = lazy(() => import('@/platform/directory/CarrierFormPage'));
const CarrierContactFormPage = lazy(() => import('@/platform/directory/CarrierContactFormPage'));
const CarrierRuleFormPage = lazy(() => import('@/platform/directory/CarrierRuleFormPage'));
const CensusLayoutFormPage = lazy(() => import('@/platform/directory/CensusLayoutFormPage'));
const UsersPage = lazy(() => import('@/platform/admin/UsersPage'));
const UserFormPage = lazy(() => import('@/platform/admin/UserFormPage'));
const UserRolesPage = lazy(() => import('@/platform/admin/UserRolesPage'));
const AuditPage = lazy(() => import('@/platform/admin/AuditPage'));
const SettingsPage = lazy(() => import('@/platform/SettingsPage'));
const NotAuthorizedPage = lazy(() => import('@/platform/NotAuthorizedPage'));
const NotFoundPage = lazy(() => import('@/platform/NotFoundPage'));

/** `/groups/:id` → `/employers/:id`, carrying the identifier across the rename. */
function LegacyGroupRedirect(): JSX.Element {
  const { employerId } = useParams();
  return <Navigate to={`/employers/${employerId ?? ''}`} replace />;
}

/** `/rfps/:id` → `/rfp/requests/:id`, carrying it across the move into the shell. */
function LegacyRfpRedirect(): JSX.Element {
  const { rfpId } = useParams();
  return <Navigate to={`/rfp/requests/${rfpId ?? ''}`} replace />;
}

function RouteFallback(): JSX.Element {
  return (
    <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '40vh' }}>
      <CircularProgress aria-label="Loading" />
    </Box>
  );
}

export function AppRoutes(): JSX.Element {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/sign-in" element={<SignInPage />} />

        <Route element={<ProtectedRoute />}>
          <Route element={<AppShell />}>
            {/* The front door. */}
            <Route index element={<HomePage />} />

            {/* Applications. */}
            {censusRoutes}
            {rfpRoutes}
            {proposalsRoutes}
            {trenchingRoutes}
            {presentationsRoutes}
            {packetsRoutes}

            {/* Directory — shared master data, deliberately not inside any application. */}
            <Route path="employers" element={<EmployersPage />} />
            <Route path="employers/new" element={<EmployerFormPage />} />
            <Route path="employers/:employerId" element={<EmployerDetailPage />} />
            <Route path="employers/:employerId/edit" element={<EmployerFormPage />} />
            <Route path="carriers" element={<CarriersPage />} />
            <Route path="carriers/new" element={<CarrierFormPage />} />
            {/* Before the `:carrierId` route, or "finder" is read as an id. */}
            <Route path="carriers/finder" element={<CarrierFinderPage />} />
            <Route path="carriers/:carrierId" element={<CarrierDetailPage />} />
            <Route path="carriers/:carrierId/edit" element={<CarrierFormPage />} />
            <Route path="carriers/:carrierId/contacts/new" element={<CarrierContactFormPage />} />
            <Route
              path="carriers/:carrierId/contacts/:contactId/edit"
              element={<CarrierContactFormPage />}
            />
            <Route path="carriers/:carrierId/rules/new" element={<CarrierRuleFormPage />} />
            <Route path="carriers/:carrierId/rules/:ruleId/edit" element={<CarrierRuleFormPage />} />
            <Route path="carriers/:carrierId/census-layout" element={<CensusLayoutFormPage />} />

            {/* Administration. */}
            <Route path="admin/users" element={<UsersPage />} />
            <Route path="admin/users/new" element={<UserFormPage />} />
            <Route path="admin/users/:userId/roles" element={<UserRolesPage />} />
            <Route path="admin/audit" element={<AuditPage />} />

            {/* Settings is personal, not administration, so it keeps its top-level URL. */}
            <Route path="settings" element={<SettingsPage />} />
          </Route>
        </Route>

        {/* Links from before the shell.
            Deliberately outside ProtectedRoute: a redirect rewrites a URL, it reads no data, and
            rewriting first means a signed-out visitor reaches sign-in carrying the new return
            path rather than one that no longer exists. */}
        <Route path="/board" element={<Navigate to="/rfp" replace />} />
        <Route path="/rfps" element={<Navigate to="/rfp/requests" replace />} />
        <Route path="/rfps/:rfpId" element={<LegacyRfpRedirect />} />
        <Route path="/users" element={<Navigate to="/admin/users" replace />} />
        <Route path="/audit" element={<Navigate to="/admin/audit" replace />} />
        {/* "Group" was the old label for an employer. */}
        <Route path="/groups" element={<Navigate to="/employers" replace />} />
        <Route path="/groups/:employerId" element={<LegacyGroupRedirect />} />

        {/* Carriers have no portal: /portal was the old carrier area and is gone. */}
        <Route path="/portal/*" element={<Navigate to="/" replace />} />
        <Route path="/not-authorized" element={<NotAuthorizedPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}
